<template>
  <!-- eslint-disable vue/no-v-html -->
  <div
    v-if="decodedContent"
    ref="el"
    class="richtext"
    v-html="decodedContent" />
  <!-- eslint-enable vue/no-v-html -->
</template>

<script lang="ts">
// source: https://github.com/nuxt/nuxtjs.org/blob/master/components/HtmlParser.vue
import type { PropType } from "vue";
import { defineComponent, showError } from "#imports";

export default defineComponent( {
  name: "RichText",
  props: {
    /**
     * HTML Content to parse
     */
    content: {
      type: String as PropType<string | null>,
      default: null,
    },
  },
  data: () => ( {
    links: [] as HTMLAnchorElement[],
  } ),
  computed: {
    decodedContent () {
      return this.content
        ? this.decodeContent( this.content ?? "" )
        : null;
    },
  },
  watch: {
    content: "contentUpdated",
  },
  mounted () {
    this.$nextTick( this.addListeners );
  },
  beforeUnmount () {
    this.removeListeners();
  },
  methods: {
    decodeContent ( value: string ) {
      const result = value;

      // Check if the value is likely URI encoded (very basic check for percent signs followed by two hex digits)
      if ( /%[0-9A-Fa-f]{2}/.test( result ) ) {
        try {
          // Try to decode as URI component first
          const decodedUriComponent = decodeURIComponent( result );

          // Optional: Further check if decoding did change the string, assume it was correctly URI decoded
          if ( decodedUriComponent !== result ) {
            return decodedUriComponent;
          }
        } catch ( e ) {
          // If URI decoding fails, log it or handle gracefully
          console.error( "Failed to decode URI component:", e );
        }
      }

      return result;
    },
    navigate ( event: MouseEvent ) {
      let target = event.target as ParentNode;
      let i = 0;
      // Go through 5 parents max to find a tag
      while (
        i < 5
        && !( target instanceof HTMLAnchorElement )
        && target?.parentNode
      ) {
        target = target.parentNode;
        i++;
      }
      // If target is still not a link, ignore
      if ( !( target instanceof HTMLAnchorElement ) ) { return; }
      return this.redirect( event, target );
    },
    async redirect ( event: MouseEvent, target: Element ) {
      const href = target.getAttribute( "href" );

      // Get link target, if local link, navigate with router link
      if ( href && href[0] === "/" ) {
        if ( !href.toLowerCase().startsWith( "/media/" ) ) {
          const linkTarget = target.getAttribute( "target" )?.toLowerCase();

          if ( !linkTarget || linkTarget === "_self" ) {
            event.preventDefault();
            await this.$router.push( href );
          }
        }
      } else if ( href === "#" ) {
        event.preventDefault();
        console.warn( "Found link with no destination, showing 404 page..." );
        await this.$router.push( { query: { missingUrl: true } } );
        showError( {
          statusCode: 404,
          message: "Page not available",
          data: {
            linkText: target.textContent,
            sourceUrl: window.location.pathname,
          },
        } );
      }
    },
    contentUpdated () {
      this.removeListeners();
      this.$nextTick( () => {
        this.addListeners();
      } );
    },
    addListeners () {
      if ( this.content ) {
        const element = this.$refs.el as HTMLDivElement;
        this.links = Array.from( element.getElementsByTagName( "a" ) );
        for ( let i = 0; i < this.links.length; i++ ) {
          this.links[i].addEventListener( "click", this.navigate, false );
        }
      }
    },
    removeListeners () {
      if ( this.links ) {
        for ( let i = 0; i < this.links.length; i++ ) {
          this.links[i].removeEventListener( "click", this.navigate, false );
        }
        this.links = [];
      }
    },
  },
} );
</script>

<style scoped lang="scss">
::v-deep( img ) {
  display: inline-block;
}

:deep( li > p:last-child ) {
  margin-bottom: 0;
}
</style>
